/**
 * Subdomain URL utilities for multi-subdomain architecture
 * Handles URL generation for app, admin, and root domains
 */

/**
 * Deployment labels that sit between a role label and the registrable domain —
 * `app.stg.lydertalk.com` is the staging app, `123.stg.lydertalk.com` a PR
 * preview of the staging site. These are part of the base domain: every
 * cross-app link and shared cookie is built from it, so dropping one sends
 * staging users to production.
 *
 * Adding an environment (`dev`, `qa`, …) means adding its label here.
 */
const ENV_LABELS = new Set(['stg']);

/**
 * Helper to get the base domain from the current hostname.
 */
const getBaseDomain = (hostname: string): string => {
    // Handle localhost
    if (hostname === 'localhost' || hostname.endsWith('.localhost')) {
        return 'localhost';
    }

    if (hostname.endsWith('.local')) {
        const parts = hostname.split('.');
        return parts.length >= 2 ? parts.slice(-2).join('.') : hostname;
    }

    // Handle preview.lydertalk.com
    if (hostname.endsWith('preview.lydertalk.com')) {
         // e.g. app.preview.lydertalk.com -> preview.lydertalk.com
         // e.g. preview.lydertalk.com -> preview.lydertalk.com

         const parts = hostname.split('.');
         // If it starts with 'app' or 'admin', strip it.
         // Note: preview.lydertalk.com has 3 parts. app.preview.lydertalk.com has 4 parts.

         if (parts[0] === 'app' || parts[0] === 'admin') {
             return parts.slice(1).join('.');
         }
         return hostname;
    }

    // Staging and its PR previews. `stg.lydertalk.com` is the staging site, and
    // `app.stg.lydertalk.com` / `123.app.stg.lydertalk.com` are deployments of
    // it — so the environment label is part of the base domain, not a subdomain.
    // Collapsing it to the registrable domain (the old behaviour) pointed every
    // cross-app link built on staging — getAppUrl, getAdminUrl, getRootUrl and
    // getCookieDomain — at production, and made staging share the production
    // session-hint cookie.
    const labels = hostname.split('.');
    const envIndex = labels.findIndex(
        (label, index) => ENV_LABELS.has(label) && index <= labels.length - 2,
    );
    if (envIndex !== -1) {
        return labels.slice(envIndex).join('.');
    }

    // Standard production (e.g. app.lydertalk.com -> lydertalk.com)
    if (labels.length === 2) {
        // Root domain (e.g., lydertalk.com)
        return hostname;
    } else if (labels.length >= 3) {
        // Has subdomain - extract base domain (last 2 parts)
        return labels.slice(-2).join('.');
    }

    return hostname;
}

/**
 * Get the app subdomain URL with the provided path
 * Handles both localhost and production environments
 * 
 * @param path - The path to append (e.g., '/auth/signin', '/session/abc')
 * @returns The complete URL pointing to the app subdomain
 * 
 * @example
 * // On localhost:8080
 * getAppUrl('/auth/signin') // => 'http://app.localhost:8080/auth/signin'
 * 
 * // On lydertalk.com
 * getAppUrl('/session/abc') // => 'https://app.lydertalk.com/session/abc'
 */
export const getAppUrl = (path: string = ''): string => {
    if (typeof window === 'undefined') {
        // SSR fallback - return relative path
        return path;
    }

    const { protocol, hostname, port } = window.location;

    // Normalize the path to ensure it starts with /
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;

    // Handle localhost scenarios
    const isLocalhost = hostname === 'localhost' || hostname.endsWith('.localhost');

    if (isLocalhost) {
        // For localhost, construct app.localhost:3001
        // If we are on port 3000 (root) or 3002 (admin), we want to go to 3001
        // But if we use 'app.localhost' hostname, we might need to handle ports carefully
        // Typically local dev uses:
        // localhost:3000 -> www
        // localhost:3001 -> app
        // localhost:3002 -> admin
        
        // So we should return localhost:3001
        return `${protocol}//localhost:3001${normalizedPath}`;
    }

    const baseDomain = getBaseDomain(hostname);

    // Construct the app subdomain URL
    const portSuffix = port && port !== '443' && port !== '80' ? `:${port}` : '';

    return `${protocol}//app.${baseDomain}${portSuffix}${normalizedPath}`;
};

/**
 * Get the root domain URL (marketing/landing page)
 * 
 * @param path - The path to append
 * @returns The complete URL pointing to the root domain
 */
export const getRootUrl = (path: string = ''): string => {
    if (typeof window === 'undefined') {
        return path;
    }

    const { protocol, hostname, port } = window.location;
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;

    const isLocalhost = hostname === 'localhost' || hostname.endsWith('.localhost');

    if (isLocalhost) {
        // If port is 3001 or 3002, switch to 3000 for root
        let rootPort = port;
        if (port === '3001' || port === '3002') {
            rootPort = '3000';
        }
        const portSuffix = rootPort ? `:${rootPort}` : '';
        return `${protocol}//localhost${portSuffix}${normalizedPath}`;
    }

    const baseDomain = getBaseDomain(hostname);
    const portSuffix = port && port !== '443' && port !== '80' ? `:${port}` : '';

    return `${protocol}//${baseDomain}${portSuffix}${normalizedPath}`;
};

/**
 * Get the admin subdomain URL
 * 
 * @param path - The path to append
 * @returns The complete URL pointing to the admin subdomain
 */
export const getAdminUrl = (path: string = ''): string => {
    if (typeof window === 'undefined') {
        return path;
    }

    const { protocol, hostname, port } = window.location;
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;

    const isLocalhost = hostname === 'localhost' || hostname.endsWith('.localhost');

    if (isLocalhost) {
        // For localhost, construct admin.localhost:3002
        // Typically local dev uses:
        // localhost:3000 -> www
        // localhost:3001 -> app
        // localhost:3002 -> admin
        
        return `${protocol}//localhost:3002${normalizedPath}`;
    }

    const baseDomain = getBaseDomain(hostname);
    const portSuffix = port && port !== '443' && port !== '80' ? `:${port}` : '';

    return `${protocol}//admin.${baseDomain}${portSuffix}${normalizedPath}`;
};

/**
 * Get the base domain for cookies to allow sharing across subdomains
 *
 * @returns The base domain string (e.g., '.lydertalk.com', '.lydertalk.local', or '.localhost')
 */
export const getCookieDomain = (): string => {
    if (typeof window === 'undefined') return '';

    const { hostname } = window.location;

    // Handle localhost (use .localhost to share cookies across ports)
    if (hostname === 'localhost' || hostname.endsWith('.localhost')) {
        return '.localhost';
    }

    // Handle lydertalk.local development domain
    if (hostname.endsWith('lydertalk.local')) {
        return '.lydertalk.local';
    }

    // Handle preview.lydertalk.com
    if (hostname.endsWith('preview.lydertalk.com')) {
         return `.${getBaseDomain(hostname)}`;
    }

    // Production, staging and preview deployments — share the cookie on the
    // deployment's base domain. Staging resolves to `.stg.lydertalk.com`, so a
    // staging session is never shared with production.
    const baseDomain = getBaseDomain(hostname);
    return baseDomain.includes('.') ? `.${baseDomain}` : baseDomain;
};

/**
 * Check if the current environment is local (localhost or .local)
 */
export const isLocal = (): boolean => {
    if (typeof window === 'undefined') return true;
    const { hostname } = window.location;
    return (
        hostname === 'localhost' ||
        hostname.endsWith('.localhost') ||
        hostname.endsWith('.local')
    );
};

/**
 * Get the current subdomain from the hostname
 * 
 * @returns The subdomain (e.g., 'app', 'admin') or null if on root domain
 * 
 * @example
 * // On app.lydertalk.com
 * getSubdomain() // => 'app'
 * 
 * // On lydertalk.com
 * getSubdomain() // => null
 * 
 * // On localhost:3001
 * getSubdomain() // => null (localhost uses ports, not subdomains)
 */
export const getSubdomain = (): string | null => {
    if (typeof window === 'undefined') return null;

    const { hostname, port } = window.location;

    // For localhost, determine "subdomain" based on port
    if (hostname === 'localhost') {
        if (port === '3001') return 'app';
        if (port === '3002') return 'admin';
        return null; // Port 3000 or others = root
    }

    // Handle .localhost subdomains (e.g., app.localhost)
    if (hostname.endsWith('.localhost')) {
        const parts = hostname.split('.');
        if (parts.length >= 2 && parts[0] !== 'localhost') {
            return parts[0];
        }
        return null;
    }

    // Handle preview.lydertalk.com
    if (hostname.endsWith('preview.lydertalk.com')) {
        const parts = hostname.split('.');
        if ((parts[0] === 'app' || parts[0] === 'admin') && parts.length > 3) {
            return parts[0];
        }
        return null;
    }

    // Standard production
    const parts = hostname.split('.');
    if (parts.length >= 3) {
        // Has subdomain
        return parts[0];
    }

    return null;
};
